/**
 * TicketFormatoSelector — Selector del formato del ticket térmico
 * (Completo / Compacto / Ultra: ahorro de papel).
 *
 * QUÉ HACE:
 *  - Muestra los TRES formatos en tarjetas compactas (icono + título +
 *    descripción breve), sin miniaturas estáticas. Cada tarjeta tiene su botón
 *    «Ver ejemplo (térmica real)» que abre `TicketFormatoEjemploModal`: la
 *    MISMA venta demo pasada por los builders reales (con los datos del
 *    negocio, logo y QR) para decidir viendo el ticket de verdad.
 *  - Cada tarjeta es seleccionable (controlado: `value` + `onChange`).
 *
 * QUÉ EXPORTA: TicketFormatoSelector (named)
 *
 * QUÉ NO DEBE HACER:
 *  - No persiste estado ni conoce de impresoras/config del negocio.
 *  - No realiza llamadas a API ni hardware nativo.
 *  - No incluye imágenes ASCII estáticas: el ejemplo real vive en
 *    `TicketFormatoEjemploModal`.
 *
 * REGLAS:
 *  - Valores: "completo" | "compacto" | "ultracompacto"
 *    (TicketFormato en businessConfigStorage).
 *  - No anidar <button>: la tarjeta es un <div> con el área de selección y un
 *    botón propio «Ver ejemplo» debajo.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Check, Eye, Table, Rows3, Columns3 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { TicketFormato } from "../utils/businessConfigStorage";
import { TicketFormatoEjemploModal } from "./TicketFormatoEjemploModal";

interface Props {
  value: TicketFormato;
  onChange: (next: TicketFormato) => void;
  className?: string;
}

interface FormatoMeta {
  value: TicketFormato;
  title: string;
  icon: LucideIcon;
  bullets: ReactNode[];
}

const FORMATOS_META: FormatoMeta[] = [
  {
    value: "completo",
    title: "Completo",
    icon: Table,
    bullets: [
      "Cada producto ocupa varias líneas con rejilla.",
      "P.UNIT, IVA, descuentos y variantes en líneas separadas.",
      "Más papel por ticket.",
    ],
  },
  {
    value: "compacto",
    title: "Compacto",
    icon: Rows3,
    bullets: [
      "Una línea por producto con su total.",
      "SKU, P.UNIT y variantes resumidas en 1 línea.",
      <>
        Ahorra{" "}
        <strong className="font-bold text-emerald-700 dark:text-emerald-400">
          ~40–60% de papel
        </strong>{" "}
        en ventas estándar.
      </>,
    ],
  },
  {
    value: "ultracompacto",
    title: "Ultra Compacto",
    icon: Columns3,
    bullets: [
      "Columnas `# CANT DESCRIP VALOR` (sin P.UNIT ni código).",
      "Descripción recortada; variantes y adicionales en línea aparte.",
      <>
        <strong className="font-bold text-emerald-700 dark:text-emerald-400">
          Máximo ahorro de papel
        </strong>{" "}
        en ventas estándar.
      </>,
    ],
  },
];

export const TicketFormatoSelector = ({
  value,
  onChange,
  className = "",
}: Props) => {
  const [ejemploFormato, setEjemploFormato] = useState<TicketFormato | null>(
    null,
  );

  return (
    <div
      className={`grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-2.5 ${className}`}
    >
      {FORMATOS_META.map((meta) => {
        const selected = value === meta.value;
        const Icon = meta.icon;
        return (
          <div
            key={meta.value}
            className={`flex flex-col overflow-hidden rounded-xl border text-left transition-colors ${
              selected
                ? "border-primary-500 bg-primary-50/60 shadow-sm dark:bg-primary-950/30"
                : "border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-500"
            }`}
          >
            {/* Área de selección */}
            <button
              type="button"
              onClick={() => onChange(meta.value)}
              aria-pressed={selected}
              className="flex flex-1 flex-col gap-1.5 px-3 pb-2 pt-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
            >
              {/* Cabecera: icono + título + estado */}
              <span className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                  <Icon
                    size={13}
                    className={`shrink-0 ${
                      selected
                        ? "text-primary-600 dark:text-primary-400"
                        : "text-gray-400 dark:text-slate-500"
                    }`}
                    aria-hidden
                  />
                  <span
                    className={`truncate text-[11px] font-black leading-none sm:text-xs ${
                      selected
                        ? "text-primary-700 dark:text-primary-300"
                        : "text-gray-800 dark:text-slate-100"
                    }`}
                  >
                    {meta.title}
                  </span>
                </span>

                <span
                  className={`grid h-4 w-4 shrink-0 place-items-center rounded-full transition-all ${
                    selected
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-transparent dark:bg-slate-700"
                  }`}
                  aria-hidden
                >
                  <Check size={10} strokeWidth={3} />
                </span>
              </span>

              {/* Descripción */}
              <span className="space-y-1 text-[10px] leading-snug text-gray-600 dark:text-slate-300">
                {meta.bullets.map((b, i) => (
                  <span key={i} className="block">
                    {b}
                  </span>
                ))}
              </span>
            </button>

            {/* Ver ejemplo real */}
            <span
              className={`border-t px-3 py-1.5 ${
                selected
                  ? "border-primary-100 dark:border-primary-900/50"
                  : "border-gray-100 dark:border-slate-800"
              }`}
            >
              <button
                type="button"
                onClick={() => setEjemploFormato(meta.value)}
                className={`inline-flex w-full items-center justify-center gap-1 text-[10px] font-semibold transition-colors ${
                  selected
                    ? "text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                    : "text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                Ver ejemplo
                <Eye size={12} className="shrink-0" aria-hidden />
              </button>
            </span>
          </div>
        );
      })}

      {ejemploFormato && (
        <TicketFormatoEjemploModal
          initialFormato={ejemploFormato}
          onClose={() => setEjemploFormato(null)}
        />
      )}
    </div>
  );
};
